import { Atom, computed } from 'mobx'
import { isEqual } from 'lodash'
import { i18n } from '../../shared/utils'
import { deepGet, deepSet } from '../../shared/obj'

// type TPhId = string | number

export default class Placeholder extends Atom {
  /**
   * Same as `Placeholder#toJSON()` but as a `@computed` getter, thus allowing
   * to listen for changes when using it.
   */
  @computed get asJSON() {
    this.reportObserved()
    return this.toJSON()
  }

  /**
   * Creates a new Placeholder Atom.
   */
  constructor(phId, value, lang, parentPlaceholderAccessor) {
    // Call the Atom constructor with a speaking name
    super(
      `Placeholder ${phId} on Article "${parentPlaceholderAccessor.article.name}" [${parentPlaceholderAccessor.article.id}]`
    )

    this.phId = phId
    this.value = value
    this.defaultLang = lang
    this.placeholderAccessor = parentPlaceholderAccessor
  }

  /**
   * Transforms the data of the Placeholder into JSON format.
   */
  toJSON() {
    return this.value
  }

  /**
   * This is the same as `Placeholder#get()` with the sole
   * difference being that the function call will not be observed.
   * @param {String?} key - The key path within the object to the
   * value that should be returned. If no path is given 'value' will be used.
   * @param {String?} lang - The language to receive the value in. If no
   * language is given, `Placeholder#defaultLang` will be used
   * @return {Any} - The value at given key. `undefined` if not set.
   */
  steal(key, lang) {
    lang = lang || this.defaultLang
    key = key || this.getKey()

    let retval = i18n(this.value, key, lang)

    if (!retval && (key === 'value.h' || key === 'value.w')) {
      // TODO: remove once the hh/ww issue is resolved
      retval = i18n(
        this.value,
        key === 'value.h' ? 'value.hh' : 'value.ww',
        lang
      )
    }

    return retval
  }

  /**
   * Retreives the value at given key path for given language.
   * @param {String?} key - The key path within the object to the
   * value that should be returned. If no path is given 'value' will be used.
   * @param {String?} lang - The language to receive the value in. If no
   * language is given, `Placeholder#defaultLang` will be used
   * @return {Any} - The value at given key. `undefined` if not set.
   */
  get(key, lang) {
    this.reportObserved()

    const retval = this.steal(key, lang)

    return retval
  }

  getSource() {
    return this.value && this.value.source
  }

  // Editor State is Draft JS specific
  getEditorState(lang) {
    lang = lang || this.defaultLang
    return i18n(this.value, 'editorState', lang)
  }

  /**
   * Helper to check if a value is effectively empty HTML
   * @param {any} val - The value to check
   * @return {boolean} - True if value is empty or equivalent to empty HTML
   */
  isEffectivelyEmpty(val) {
    if (!val) return true
    if (typeof val === 'string') {
      return val === '<p></p>' || val === '<p><br></p>'
    }
    return false
  }

  /**
   *
   * @param {String?} key
   * @param {any?} valueObj
   * @param {String?} lang
   * @returns {Promise<Placeholder>}
   */
  set(key, valueObj, lang) {
    lang = lang || this.defaultLang
    key = key || this.getKey()

    if (key.indexOf('hh') >= 0 || key.indexOf('ww') > 0) {
      // eslint-disable-next-line
      debugger;
    }

    if (valueObj === null) {
      valueObj = { value: null }
    }
    else if (typeof valueObj === 'string') {
      valueObj = { value: valueObj, type: 'text' }
    }
    else if (typeof valueObj === 'object' && !('value' in valueObj)) {
      valueObj = { value: valueObj, type: 'keyValue' }
    }

    let newValue = valueObj.value
    const newSource = valueObj.source || null
    const newType = valueObj.type

    // BAIL OUT EARLY if placeholder types mismatch
    if (
      newType
      && this.value
      && this.value.type
      && newType !== this.value.type
    ) {
      log.placeholder(
        `Placeholder.set(): You are trying to change the placeholder type from "${this.value.type}" to "${newType}". Changing the type is not allowed => skipping!"`
      )
      return Promise.resolve(this)
    }

    // Get old value from the i18n structure (handles both Scribe and Draft.js formats)
    const oldValueData = this.value ? deepGet(this.value, `i18n.${lang}`, undefined) : undefined
    const oldValue = oldValueData && oldValueData.value !== undefined ? oldValueData.value : oldValueData

    // Check if values are equal (including effectively empty HTML check)
    const bothEffectivelyEmpty = this.isEffectivelyEmpty(oldValue) && this.isEffectivelyEmpty(newValue)
    const valuesEqual = isEqual(oldValue, newValue) || bothEffectivelyEmpty

    // check if sources are equal (Censhare source)
    let sourcesEqual = false
    if (this.value && this.value.source && newSource) {
      sourcesEqual = this.value.source.id === newSource.id
    }
    else if ((!this.value || !this.value.source) && !newSource) {
      sourcesEqual = true
    }

    // BAIL OUT EARLY if value hasn't changed
    if (valuesEqual && sourcesEqual) {
      log.placeholder(
        `Placeholder.set(): old value equals new value ("${newValue}") => skipping!`
      )
      return Promise.resolve(this)
    }

    // Update the value
    if (this.value) {
      key = key.replace('value.', '')
      deepSet(this.value, `i18n.${lang}${key ? `.${key}` : ''}`, newValue)
      this.value.source = newSource

      // never allow changing the value once it was set
      if (!this.value.type) {
        this.value.type = newType
      }
    }
    else {
      newValue = i18n({}, key, lang, newValue || '')
      newValue.type = newType
      this.value = newValue
      this.value.source = newSource
    }

    // undefined for scribe editor
    // null or array for draft editor
    let isDraft = false
    if (valueObj.editorState || valueObj.editorState === null) {
      deepSet(this.value, `i18n.${lang}.editorState`, valueObj.editorState)
      isDraft = true
    }

    this.reportChanged()
    this.placeholderAccessor.reportChanged()
    return this.placeholderAccessor.article.saveWithDebounce({ noBackendSet: true })
      .then(() => {
        return this
      })
      .catch((err) => {
        console.error('Placeholder.set(): Error during saveWithDebounce:', err)
        throw err
      })
  }

  destroy() {
    this.value = null
    this.placeholderAccessor.reportChanged()
    this.reportChanged()
  }

  /**
   *
   * @param {String?} key
   * @param {String?} lang
   */
  isEmpty(key = this.getKey(), lang = this.defaultLang) {
    const value = i18n(this.value, key, lang)
    return !value
  }

  getKey() {
    const type = this.getType()
    switch (type) {
      case 'image':
        return 'value.src'
      case 'video':
        return 'value'
      case 'text':
        return 'value'
      default:
        return 'value'
    }
  }

  /**
   * Get's the type of given placeholder.
   */
  getType() {
    return (this.value && this.value.type) || null
  }

  setType(type) {
    if (this.value) {
      this.value.type = type
    }
    return this
  }
}
